import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { __ } from '@wordpress/i18n';
import parse from 'html-react-parser';

import { GRANT_SHAPE, TAXONOMY_MAP } from 'config/prop-types';

import ExternalLink from 'images/icon-external-link.svg';

import './grantsListItem.scss';

export default class GrantsListItem extends React.PureComponent {
  /**
   * Constructor. Binds function scope.
   * @param {object} props - Props for this component.
   */
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
  }

  /**
  * Renders program link markup.
  * @returns {object} - JSX for this component.
  */
  /* Temporary removal, see KORG-1493.
    renderProgramLinks() {
      const {
        grant: {
          programs,
        },
        programAreasTaxonomy,
      } = this.props;

      const pageLinkMap = {
        education: '/education/',
        entrepreneurship: '/entrepreneurship/',
        'kc-civic': '/kansas-city-civic/',
        'other-foundation-support': false,
      };

      const grantPrograms = programs.map((programId) => {
        const { name, slug } = programAreasTaxonomy[programId];

        return {
          name,
          uri: pageLinkMap[slug],
        };
      });

      const grantMarkup = grantPrograms.map(({ name, uri }) => {
        if (uri) {
          return <a key={name} href={uri}>{name}</a>;
        }

        return <span key={name}>{name}</span>;
      });

      return grantMarkup;
    }
   */

  /**
     * Renders component markup.
     * @returns {object} - JSX for this component.
     */
  render() {
    const {
      grant,
      strategiesTaxonomy,
      statesTaxonomy,
      grantTypesTaxonomy,
    } = this.props;
    const {
      amount,
      description,
      location,
      name,
      strategies,
      states,
      grantTypes,
      url,
      year,
    } = grant;

    let state = '';
    const stateObj = statesTaxonomy[states[0]];
    if (states && stateObj && 'object' === typeof states) {
      state = stateObj.name;
    }
    const grantTypesNames =
      grantTypes.map((typeId) => grantTypesTaxonomy[typeId].name);
    const strategyNames =
      strategies.map((strategyId) => strategiesTaxonomy[strategyId].name);
    /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
    return (
      <li
        className={classNames(
          'grant-item',
          this.state.isOpen ? 'is-open' : null
        )}
        tabIndex="0"
      >
        <div className="grant-item__content">
          <div className="grant-item__details">
            <div className="grant-item__header">
              <div className="grant-item__details-org">
                <a href={url} target="_blank">
                  <span>{DOMPurify.sanitize(parse(name))}</span>
                  <img
                    src={ExternalLink}
                    alt={__('Visit organization\'s website', 'kauffman')}
                  />
                </a>
              </div>
            </div>
            <div className="grant-item__details-description">
              <p>{DOMPurify.sanitize(parse(description))}</p>
              <div
                aria-label={`Award amount $${amount.toLocaleString()}`}
                className="grant-item__amount grants-app__mobile-only"
              >
                {`$${amount.toLocaleString()}`}
              </div>
            </div>
            <div
              className={classNames(
                'grant-item__more-details',
                this.state.isOpen ? 'is-open' : null
              )}
            >
              {strategyNames.length ? (
                <div className="grant-item__strategy grants-app__mobile-only">
                  <p className="grant-item__label">
                    {__('Strategic Priority', 'kauffman')}
                  </p>
                  <p className="grant-item__detail">
                    {strategyNames.join(', ')}
                  </p>
                </div>
              ) : null}
              {year ? (
                <div className="grant-item__year grants-app__mobile-only">
                  <p className="grant-item__label">
                    {__('Year', 'kauffman')}
                  </p>
                  <p className="grant-item__detail">{year}</p>
                </div>
              ) : null}
              {grantTypesNames.length ? (
                <div className="grant-item__type">
                  <p className="grant-item__label">
                    {__('Grant Type', 'kauffman')}
                  </p>
                  <p className="grant-item__detail">
                    {grantTypesNames.join(', ')}
                  </p>
                </div>
              ) : null}
              <div className="grant-item__details-location">
                <p className="grant-item__label">
                  {__('Location', 'kauffman')}
                </p>
                <p className="grant-item__detail">
                  {
                    DOMPurify.sanitize(`${location}${state ? `, ${state}` : ''}`)
                  }
                </p>
              </div>
            </div>
            <button
              onClick={() => this.setState({ isOpen: ! this.state.isOpen })}
              className="grant-item__detail-button"
            >
              {this.state.isOpen
                ? __('Hide Details', 'kauffman')
                : __('Show Details', 'kauffman')
              }
            </button>
          </div>
          <div className="grant-item__values">
            <div
              aria-label={`Award amount $${amount.toLocaleString()}`}
              className="grant-item__amount grants-app__desktop-only"
            >
              {`$${amount.toLocaleString()}`}
            </div>
            {strategyNames.length ? (
              <div className="grant-item__strategy grants-app__desktop-only">
                <p className="grant-item__label">
                  {__('Strategic Priority', 'kauffman')}
                </p>
                <p className="grant-item__detail">{strategyNames.join(', ')}</p>
              </div>
            ) : null}
            {year ? (
              <div className="grant-item__year grants-app__desktop-only">
                <p className="grant-item__label">
                  {__('Year', 'kauffman')}
                </p>
                <p className="grant-item__detail">{year}</p>
              </div>
            ) : null}
          </div>
        </div>
      </li>
    );
    /* eslint-enable */
  }
}

GrantsListItem.propTypes = {
  grant: PropTypes.shape(GRANT_SHAPE).isRequired,
  /* Temporary removal, see KORG-1493
  programAreasTaxonomy:
    PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP)).isRequired,
    */
  grantTypesTaxonomy:
    PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP)).isRequired,
  strategiesTaxonomy:
    PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP)).isRequired,
  statesTaxonomy:
    PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP)).isRequired,
};
